import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Button, CircularProgress, Typography, Chip, Paper, Grid,
  Stack, CardActions, useTheme, useMediaQuery,
} from '@mui/material';
import { DataGrid, gridClasses, GridToolbar } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { green, blue, orange, red, grey } from '@mui/material/colors';
import { useLayout } from '../../../context/LayoutContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UndoIcon from '@mui/icons-material/Undo';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { toast } from 'react-toastify';
import { format } from 'date-fns';

import { useGetCompletedShiftsQuery, useUndoShiftActualsMutation } from '../../../slices/shiftApiSlice';
import { useGetHospitalLocationsQuery } from '../../../slices/hospitalLocationApiSlice';

// Styled DataGrid with conditional row styles
const StyledDataGrid = styled(DataGrid)`
  & .${gridClasses.row}.completedRow {
    background-color: ${green[50]};
  }
  & .${gridClasses.row}.weekendRow {
    background-color: ${orange[50]};
  }
  & .${gridClasses.row}.recentRow {
    background-color: ${blue[50]};
  }
  & .${gridClasses.row}.notWorkedRow {
    background-color: ${orange[100]};
  }
  & .${gridClasses.row}.cancelledRow {
    background-color: ${red[50]};
  }
`;

// Accent colours for the mobile card border-left, keyed by the same row
// class names the desktop grid uses (see getRowClassName below).
const ROW_ACCENT = {
  completedRow: green[400],
  recentRow: blue[400],
  notWorkedRow: orange[500],
  cancelledRow: red[400],
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return format(date, 'dd/MM/yyyy HH:mm');
  } catch (error) {
    return 'Invalid Date';
  }
};

// Helper function to calculate duration
const calculateDuration = (start, end) => {
  if (!start || !end) return 'N/A';
  try {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const durationMs = endDate - startDate;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  } catch (error) {
    return 'N/A';
  }
};

// Mandatory unpaid meal break deducted from a shift's gross duration.
// Tiers (current policy):
//   duration <= 4h  -> 0 minutes deducted
//   duration <  6h  -> 15 minutes deducted
//   duration <  8h  -> 30 minutes deducted
//   duration >= 8h  -> 60 minutes deducted
const breakMinsFor = (grossMins) => {
  const hours = grossMins / 60;
  if (hours <= 4) return 0;
  if (hours < 6) return 15;
  if (hours < 8) return 30;
  return 60;
};

// Billable duration = gross worked time minus the mandatory unpaid break for
// that duration tier. Returns a formatted "Xh Ym" string (never negative —
// floored at 0 in case of bad/short actual times), or 'N/A' if start/finish
// aren't both present.
const calculateBillableDuration = (start, end) => {
  if (!start || !end) return 'N/A';
  try {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const grossMins = (endDate - startDate) / (1000 * 60);
    const billableMins = Math.max(0, grossMins - breakMinsFor(grossMins));
    const hours = Math.floor(billableMins / 60);
    const minutes = Math.round(billableMins % 60);
    return `${hours}h ${minutes}m`;
  } catch (error) {
    return 'N/A';
  }
};

// A cancelled shift is any shift that didn't end up worked: either called
// off outright (agencyCancelled) or recorded after the fact as Not-Worked.
const isCancelledOrNotWorked = (shift) => !!shift?.agencyCancelled || shift?.actualStatus === 'Not-Worked';

// Billing rule for cancelled / not-worked shifts:
//   - Beaumont cancels/no-shows with LESS than 12h notice  -> flat 4h charge
//   - Beaumont cancels/no-shows with 12h+ notice            -> nothing billed
//   - Agency cancels/no-shows (any notice)                  -> nothing billed
// The Beaumont-vs-agency / notice-given logic is computed and stored
// server-side as `chargeApplies` (see computeChargeApplies in
// shiftController.js — both reOpenShift and recordActualFiller set it), so
// this reads that flag directly instead of re-deriving it from
// cancelledByType/advanceNoticeGiven here. That keeps this in lockstep with
// the backend rule (including its edge case: an unset advanceNoticeGiven
// counts as chargeable there, not just an explicit `false`) and needs no
// changes if the charge rule itself ever moves. cancelledByType /
// advanceNoticeGiven / chargeApplies are all new fields — shifts recorded
// before this went live won't have them set, so this simply falls through
// to "0h" for those older records either way.
const BEAUMONT_SHORT_NOTICE_CHARGE_HOURS = 4;

const beaumontShortNoticeCharge = (shift) => isCancelledOrNotWorked(shift) && shift?.chargeApplies === true;

// Billable-hours display: worked shifts show actual time minus the
// mandatory break; cancelled/not-worked shifts show either the flat 4h
// Beaumont short-notice charge or 0h (agency-cancelled, or Beaumont
// cancelled/no-showed with proper notice).
const billableDisplayFor = (shift) => {
  if (!shift) return 'N/A';
  if (isCancelledOrNotWorked(shift)) {
    return beaumontShortNoticeCharge(shift) ? `${BEAUMONT_SHORT_NOTICE_CHARGE_HOURS}h (charge)` : '0h';
  }
  return calculateBillableDuration(shift.actualStart, shift.actualFinish);
};

// The agency name to display for a shift — prefer the actual filler
// (recorded after the fact) and fall back to the planned agency. Blank
// for staff-filled shifts.
const agencyNameOf = (shift) => shift?.actualFilledByAgencyName || shift?.filledByAgencyName || '';

// A shift was "replaced" when it was superseded by a new shift as part of
// the recordActualFiller split (see shiftController.js) — distinct from an
// agencyCancelled shift, which keeps its own record and is never
// superseded. A shift can only be one or the other, never both.
const wasReplaced = (shift) => !!shift?.supersededByShiftId && !shift?.agencyCancelled;

// Human-readable outcome for the shift, per the requested wording:
// agencyCancelled takes priority (it's a locked historical record
// regardless of actualStatus), then Complete → "Worked", then Not-Worked,
// falling back to whatever actualStatus is set to (e.g. still Open).
const outcomeLabel = (shift) => {
  if (!shift) return 'N/A';
  if (shift.agencyCancelled) return 'Cancelled';
  if (shift.actualStatus === 'Complete') return 'Worked';
  if (shift.actualStatus === 'Not-Worked') return 'Not-Worked';
  return shift.actualStatus || 'N/A';
};

// Resolves supersededByShiftId to a human label naming whoever actually
// covered the replacement — an agency name if the replacement shift is
// agency-filled, or the staff member's name if it's staff-filled.
// Requires supersededByShiftId to be populated (see backend note) rather
// than left as a bare ObjectId.
const supersededByLabel = (supersededByShiftId) => {
  if (!supersededByShiftId || typeof supersededByShiftId !== 'object') return null;
  const isAgency = !!supersededByShiftId.filledByAgencyName || supersededByShiftId.lineStatus === 'FilledByAgency';
  if (isAgency) return `Agency – ${supersededByShiftId.filledByAgencyName || 'Unknown agency'}`;
  if (supersededByShiftId.filledByName) return `Staff – ${supersededByShiftId.filledByName}`;
  return 'Unknown';
};

const supersededById = (supersededByShiftId) => {
  if (!supersededByShiftId) return null;
  return typeof supersededByShiftId === 'object' ? supersededByShiftId._id : supersededByShiftId;
};

// A shift is eligible for the admin "Undo Actuals" correction only when it
// mirrors the backend guard in undoShiftActuals (shiftController.js):
// actualStatus is 'Complete' or 'Not-Worked', not agency-cancelled, and not
// superseded by a replacement shift. Cancelled and replaced shifts are
// deliberately excluded — a cancelled shift can be re-created and fixed
// directly, and a replaced shift already has its own new record to adjust
// instead. Kept here purely as a UI hint so the button doesn't even appear
// where the backend would reject it — the backend re-checks all of this
// itself regardless.
const UNDOABLE_ACTUAL_STATUSES = ['Complete', 'Not-Worked'];

const canUndoActuals = (shift) =>
  !!shift &&
  UNDOABLE_ACTUAL_STATUSES.includes(shift.actualStatus) &&
  !shift.agencyCancelled &&
  !shift.supersededByShiftId;

const ShiftCompletedListScreen = () => {
  const navigate = useNavigate();
  const { setFluid } = useLayout();
  const { userInfo } = useSelector((state) => state.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);

  // ── Undo Actuals (admin-only correction) ──────────────────────────────────
  const [openUndoConfirm, setOpenUndoConfirm] = useState(false);
  const [undoReason, setUndoReason] = useState('');
  const [undoShiftActuals, { isLoading: isUndoing }] = useUndoShiftActualsMutation();

  //const [dateRange, setDateRange] = useState({
  //  startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
  //  endDate: new Date().toISOString().split('T')[0],
  // });

  const toLocalISO = (d) => {
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const [dateRange, setDateRange] = useState({
  startDate: toLocalISO(new Date(new Date().setMonth(new Date().getMonth() - 1))),
  endDate:   toLocalISO(new Date()),
});


  // ============= MANAGER DIRECTORATE FILTERING =============
  const managerDirectorate = userInfo?.directorateName || null;
  const isManager = userInfo?.isManager === true;
  const isAdmin = userInfo?.isAdmin === true;

  console.log('User Info:', {
    isAdmin,
    isManager,
    directorate: managerDirectorate
  });

  // Fetch hospital locations
  const { data: hospitalLocations = [], isLoading: loadingLocations } = useGetHospitalLocationsQuery();

  // Create ward to directorate mapping
  const wardToDirectorateMap = useMemo(() => {
    if (!hospitalLocations || hospitalLocations.length === 0) return {};

    const map = {};
    hospitalLocations.forEach(location => {
      if (location.wardDepartmentArea && location.directorate) {
        map[location.wardDepartmentArea] = location.directorate;
      }
    });
    return map;
  }, [hospitalLocations]);

  // Fetch completed shifts
  const {
    data: shifts,
    isLoading,
    isError,
    error,
    refetch
  } = useGetCompletedShiftsQuery({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    status: 'Complete'
  });

  // Filter shifts based on manager's directorate
  const filteredShifts = useMemo(() => {
    if (!shifts) return [];

    // Admin sees all shifts
    if (isAdmin) return shifts;

    // Manager sees only shifts in their directorate
    if (isManager && managerDirectorate) {
      const filtered = shifts.filter(shift => {
        const shiftWard = shift.wardDepartmentArea;
        if (!shiftWard) return false;

        const wardDirectorate = wardToDirectorateMap[shiftWard];
        return wardDirectorate === managerDirectorate;
      });

      console.log(`Filtered completed shifts for directorate "${managerDirectorate}": ${filtered.length} of ${shifts.length}`);
      return filtered;
    }

    return shifts;
  }, [shifts, isAdmin, isManager, managerDirectorate, wardToDirectorateMap]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!filteredShifts || filteredShifts.length === 0) {
      return {
        totalShifts: 0,
        totalHours: 0,
        uniqueStaff: 0,
        uniqueWards: 0,
        cancelledCount: 0,
        notWorkedCount: 0,
        chargesDueCount: 0,
      };
    }

    const totalHours = filteredShifts.reduce((acc, shift) => {
      const duration = calculateDuration(shift.actualStart, shift.actualFinish);
      const hours = parseFloat(duration.split('h')[0]) || 0;
      return acc + hours;
    }, 0);

    const uniqueStaff = new Set(filteredShifts.map(s => s.actualPersonWorked).filter(Boolean)).size;
    const uniqueWards = new Set(filteredShifts.map(s => s.wardDepartmentArea).filter(Boolean)).size;
    const cancelledCount  = filteredShifts.filter(s => s.agencyCancelled).length;
    const notWorkedCount  = filteredShifts.filter(s => !s.agencyCancelled && s.actualStatus === 'Not-Worked').length;
    const chargesDueCount = filteredShifts.filter(s => s.chargeApplies === true).length;

    return {
      totalShifts: filteredShifts.length,
      totalHours: totalHours.toFixed(1),
      uniqueStaff,
      uniqueWards,
      cancelledCount,
      notWorkedCount,
      chargesDueCount,
    };
  }, [filteredShifts]);



const getReasonColour = (value) => {
  if (value === '1-1 special') return '#f44336';
  if (value === 'Cohorts')     return '#ff9800';
  return undefined;
};



  const columns = [
    {
      field: 'actualStart',
      headerName: 'Actual Start',
      width: 140,
      valueGetter: (params) => params.row.actualStart,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'actualFinish',
      headerName: 'Actual Finish',
      width: 140,
      valueGetter: (params) => params.row.actualFinish,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'duration',
      headerName: 'Duration',
      width: 90,
      valueGetter: (params) => calculateDuration(params.row.actualStart, params.row.actualFinish),
    },
    {
      field: 'billableHours',
      headerName: 'Billable Hours',
      width: 140,
      valueGetter: (params) => billableDisplayFor(params.row),
      renderCell: (params) => {
        const isCharge = beaumontShortNoticeCharge(params.row);
        const isZeroBilled = isCancelledOrNotWorked(params.row) && !isCharge;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonetizationOnIcon fontSize="small" color={isCharge ? 'error' : isZeroBilled ? 'disabled' : 'action'} />
            <Typography
              variant="body2"
              color={isCharge ? 'error' : isZeroBilled ? 'text.disabled' : 'text.primary'}
              fontWeight={isCharge ? 'bold' : 'normal'}
            >
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'outcome',
      headerName: 'Outcome',
      width: 120,
      valueGetter: (params) => outcomeLabel(params.row),
      renderCell: (params) => {
        const value = params.value;
        const color = value === 'Worked' ? 'success' : value === 'Not-Worked' ? 'warning' : value === 'Cancelled' ? 'error' : 'default';
        return <Chip label={value} size="small" color={color} variant={value === 'Worked' ? 'filled' : 'outlined'} />;
      },
    },
    {
      field: 'wardDepartmentArea',
      headerName: 'Ward/Dept',
      width: 130,
    },
    {
      field: 'actualPersonWorked',
      headerName: 'Staff Member',
      width: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.value || 'N/A'}</Typography>
        </Box>
      ),
    },

    {
      field: 'approvedBy',
      headerName: 'Approved By',
      width: 160,
      valueGetter: (params) => params.row.stage2WardApprovalManager?.name || 'N/A',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },

    {
      field: 'staffOrAgency',
      headerName: 'Staff / Agency',
      width: 110,
      valueGetter: (params) => {
        const isAgency = !!(params.row.actualFilledByAgency || params.row.filledByAgency);
        return isAgency ? 'Agency' : 'Staff';
      },
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'Agency' ? 'secondary' : 'default'}
          variant={params.value === 'Agency' ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'agencyName',
      headerName: 'Agency Name',
      width: 170,
      valueGetter: (params) => agencyNameOf(params.row),
      renderCell: (params) => (
        params.value
          ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="body2">{params.value}</Typography>
            </Box>
          )
          : <Typography variant="body2" color="text.disabled">—</Typography>
      ),
    },
    {
      field: 'poNumber',
      headerName: 'PO Number',
      width: 130,
      renderCell: (params) => (
        params.value
          ? <Typography variant="body2" fontWeight="bold">{params.value}</Typography>
          : <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>
      ),
    },
    {
      field: 'cancelledByType',
      headerName: 'Cancelled By',
      width: 130,
      valueGetter: (params) => (params.row.agencyCancelled ? (params.row.cancelledByType || 'Unknown') : ''),
      renderCell: (params) => (
        params.value
          ? <Chip label={params.value} size="small" color={params.value === 'Beaumont' ? 'error' : 'default'} variant="outlined" />
          : <Typography variant="body2" color="text.disabled">—</Typography>
      ),
    },
    {
      field: 'replacedBy',
      headerName: 'Replaced By',
      width: 190,
      valueGetter: (params) => supersededByLabel(params.row.supersededByShiftId) || '',
      renderCell: (params) => {
        if (!params.value) {
          return <Typography variant="body2" color="text.disabled">—</Typography>;
        }
        const id = supersededById(params.row.supersededByShiftId);
        return (
          <Chip
            icon={<SwapHorizIcon fontSize="small" />}
            label={params.value}
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              if (id) navigate(`/${isAdmin ? 'admin' : 'manager'}/shiftsWard/${id}/edit`);
            }}
            sx={{ cursor: id ? 'pointer' : 'default' }}
          />
        );
      },
    },
    {
      field: 'chargeApplies',
      headerName: 'Charge Applicable',
      width: 150,
      valueGetter: (params) => !!params.row.chargeApplies,
      renderCell: (params) => (
        params.value
          ? <Chip icon={<MonetizationOnIcon fontSize="small" />} label="Charge Due" size="small" color="error" />
          : <Typography variant="body2" color="text.disabled">—</Typography>
      ),
    },


    {
      field: 'categoryOfStaff',
      headerName: 'Category',
      width: 110,
    },






    {
  field: 'reasonForRequest',
  headerName: 'Shift Type',
  width: 130,
  renderCell: (params) => {
    const value = params.row.reasonForRequest;
    const colour = getReasonColour(value);
    return (
      <Chip
        label={value || 'N/A'}
        size="small"
        variant="outlined"
        sx={{
          borderColor: colour,
          color: colour,
        }}
      />
    );
  },
},


    {
      field: 'specialsReason',
      headerName: '1-1 / Cohort Reason',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value || 'N/A'}
          size="small"
          color={params.value === 'Short Notice' ? 'warning' : 'default'}
          variant="outlined"
        />
      ),
    },



    {
      field: 'scheduledStart',
      headerName: 'Scheduled Start',
      width: 140,
      valueGetter: (params) => params.row.startDateTime,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'scheduledEnd',
      headerName: 'Scheduled End',
      width: 140,
      valueGetter: (params) => params.row.endDateTime,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'directorate',
      headerName: 'Directorate',
      width: 130,
      valueGetter: (params) => wardToDirectorateMap[params.row.wardDepartmentArea] || 'N/A',
    },
  ];

  const getRowClassName = (params) => {
    const row = params.row;

    // Cancelled / Not-Worked take priority over the generic "recent" /
    // "completed" styling below, since they represent a different outcome
    // entirely rather than just a timing distinction.
    if (row.agencyCancelled) return 'cancelledRow';
    if (row.actualStatus === 'Not-Worked') return 'notWorkedRow';

    const now = new Date();
    const finishDate = new Date(row.actualFinish);
    const daysDiff = (now - finishDate) / (1000 * 60 * 60 * 24);

    if (!isNaN(daysDiff) && daysDiff <= 7) return 'recentRow';
    return 'completedRow';
  };

  const handleRowClick = (params) => {
    setSelectedShift(params.row);
    setOpenDetailsModal(true);
  };

  const handleDateRangeChange = (field) => (event) => {
    setDateRange(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleRefresh = () => {
    refetch();
    toast.success('Data refreshed');
  };

  // ── Undo Actuals handlers ──────────────────────────────────────────────────
  const handleOpenUndoConfirm = () => {
    setUndoReason('');
    setOpenUndoConfirm(true);
  };

  const handleCloseUndoConfirm = () => {
    if (isUndoing) return;
    setOpenUndoConfirm(false);
  };

  const handleConfirmUndoActuals = async () => {
    if (!selectedShift?._id) return;
    try {
      await undoShiftActuals({ shiftId: selectedShift._id, reason: undoReason }).unwrap();
      toast.success('Actuals undone — the shift has been stepped back to Open and is ready to be re-approved.');
      setOpenUndoConfirm(false);
      setOpenDetailsModal(false);
      setSelectedShift(null);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Failed to undo actuals');
    }
  };

  useEffect(() => {
    setFluid(true);
    return () => {
      setFluid(false);
    };
  }, [setFluid]);

  if (isLoading || loadingLocations) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography color="error">Error: {error?.message || 'Failed to load completed shifts'}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: isMobile ? 'auto' : 'calc(100vh - 100px)', width: '100%', maxWidth: '100vw', overflowX: 'hidden', p: 2, boxSizing: 'border-box' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Completed Shifts
          </Typography>
          {isManager && managerDirectorate && (
            <Typography variant="subtitle1" color="primary">
              Viewing completed shifts for directorate: {managerDirectorate}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, width: isMobile ? '100%' : 'auto' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRefresh}
            fullWidth={isMobile}
          >
            Refresh Data
          </Button>
        </Box>
      </Box>

      {/* Date Range Filter and Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Date Range
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={handleDateRangeChange('startDate')}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  width: '100%'
                }}
              />
              <input
                type="date"
                value={dateRange.endDate}
                onChange={handleDateRangeChange('endDate')}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  width: '100%'
                }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Statistics Cards */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={4} sm={2}>
              <Card sx={{ bgcolor: green[500], color: 'white' }}>
                <CardContent>
                  <Typography variant="h4">{stats.totalShifts}</Typography>
                  <Typography variant="body2">Total Shifts</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4} sm={2}>
              <Card sx={{ bgcolor: blue[500], color: 'white' }}>
                <CardContent>
                  <Typography variant="h4">{stats.totalHours}</Typography>
                  <Typography variant="body2">Hours Worked</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4} sm={2}>
              <Card sx={{ bgcolor: orange[500], color: 'white' }}>
                <CardContent>
                  <Typography variant="h4">{stats.uniqueStaff}</Typography>
                  <Typography variant="body2">Unique Staff</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4} sm={2}>
              <Card sx={{ bgcolor: grey[700], color: 'white' }}>
                <CardContent>
                  <Typography variant="h4">{stats.uniqueWards}</Typography>
                  <Typography variant="body2">Wards Covered</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4} sm={2}>
              <Card sx={{ bgcolor: red[600], color: 'white' }}>
                <CardContent>
                  <Typography variant="h4">{stats.cancelledCount}</Typography>
                  <Typography variant="body2">Cancelled</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4} sm={2}>
              <Card sx={{ bgcolor: '#8e44ad', color: 'white' }}>
                <CardContent>
                  <Typography variant="h4">{stats.chargesDueCount}</Typography>
                  <Typography variant="body2">Charges Due</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {isMobile ? (
        /* ── Mobile card list ── */
        <Stack spacing={1.5}>
          {filteredShifts.length === 0 && (
            <Typography color="textSecondary" align="center" sx={{ mt: 4 }}>
              No completed shifts to show.
            </Typography>
          )}
          {(filteredShifts || []).map((shift) => {
            const rowClass = getRowClassName({ row: shift });
            const accent = ROW_ACCENT[rowClass] || grey[400];
            const outcome = outcomeLabel(shift);
            const outcomeColor = outcome === 'Worked' ? 'success' : outcome === 'Not-Worked' ? 'warning' : outcome === 'Cancelled' ? 'error' : 'default';
            const isCharge = beaumontShortNoticeCharge(shift);
            const isZeroBilled = isCancelledOrNotWorked(shift) && !isCharge;
            const isAgency = !!(shift.actualFilledByAgency || shift.filledByAgency);

            return (
              <Card
                key={shift._id}
                elevation={2}
                sx={{ borderLeft: `6px solid ${accent}` }}
              >
                <CardContent
                  sx={{ pb: 1, cursor: 'pointer' }}
                  onClick={() => handleRowClick({ row: shift })}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {shift.wardDepartmentArea || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {wardToDirectorateMap[shift.wardDepartmentArea] || 'N/A'}
                      </Typography>
                    </Box>
                    <Chip label={outcome} size="small" color={outcomeColor} variant={outcome === 'Worked' ? 'filled' : 'outlined'} />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">{shift.actualPersonWorked || 'N/A'}</Typography>
                    <Chip
                      label={isAgency ? 'Agency' : 'Staff'}
                      size="small"
                      color={isAgency ? 'secondary' : 'default'}
                      variant={isAgency ? 'filled' : 'outlined'}
                      sx={{ ml: 'auto' }}
                    />
                  </Box>
                  {isAgency && agencyNameOf(shift) && (
                    <Typography variant="body2" color="textSecondary">
                      <strong>Agency:</strong> {agencyNameOf(shift)}
                    </Typography>
                  )}

                  <Typography variant="body2" color="textSecondary">
                    <strong>Actual:</strong> {formatDate(shift.actualStart)} – {formatDate(shift.actualFinish)} ({calculateDuration(shift.actualStart, shift.actualFinish)})
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <MonetizationOnIcon fontSize="small" color={isCharge ? 'error' : isZeroBilled ? 'disabled' : 'action'} />
                    <Typography
                      variant="body2"
                      color={isCharge ? 'error' : isZeroBilled ? 'text.disabled' : 'text.primary'}
                      fontWeight={isCharge ? 'bold' : 'normal'}
                    >
                      Billable: {billableDisplayFor(shift)}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Typography variant="body2">
                    <strong>Category:</strong> {shift.categoryOfStaff || '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Shift Type:</strong> {shift.reasonForRequest || '—'}
                  </Typography>
                  {shift.poNumber && (
                    <Typography variant="body2">
                      <strong>PO Number:</strong> {shift.poNumber}
                    </Typography>
                  )}
                  {shift.chargeApplies && (
                    <Chip
                      icon={<MonetizationOnIcon fontSize="small" />}
                      label="Charge Due"
                      size="small"
                      color="error"
                      sx={{ mt: 1 }}
                    />
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 1.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityIcon fontSize="small" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick({ row: shift });
                    }}
                  >
                    View Details
                  </Button>
                </CardActions>
              </Card>
            );
          })}
        </Stack>
      ) : (
        /* Data Grid */
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <StyledDataGrid
          density="compact"
          rows={filteredShifts || []}
          columns={columns}
          getRowId={(row) => row._id}
          getRowClassName={getRowClassName}
          initialState={{
            pagination: {
              pageSize: 10,
            },
            sorting: {
              sortModel: [{ field: 'actualFinish', sort: 'desc' }],
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          disableSelectionOnClick
          onRowClick={handleRowClick}
        />
        </Box>
      )}

      {/* Details Modal */}
      <Dialog
        open={openDetailsModal}
        onClose={() => setOpenDetailsModal(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Shift Details
          <IconButton
            aria-label="close"
            onClick={() => setOpenDetailsModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedShift && (
            <Box>
              {/* Status Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                {selectedShift.agencyCancelled ? (
                  <>
                    <CancelIcon sx={{ color: red[500], fontSize: 40 }} />
                    <Typography variant="h6" color="error.main">
                      Agency Shift Cancelled
                    </Typography>
                  </>
                ) : selectedShift.actualStatus === 'Not-Worked' ? (
                  <>
                    <ErrorOutlineIcon sx={{ color: orange[500], fontSize: 40 }} />
                    <Typography variant="h6" sx={{ color: orange[800] }}>
                      Shift Recorded as Not-Worked
                    </Typography>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon sx={{ color: green[500], fontSize: 40 }} />
                    <Typography variant="h6" color="success.main">
                      Shift Completed Successfully
                    </Typography>
                  </>
                )}
                {selectedShift.chargeApplies && (
                  <Chip
                    icon={<MonetizationOnIcon />}
                    label="Charge Applicable"
                    color="error"
                    size="small"
                  />
                )}

                {/* Admin-only: undo actuals / approval — only shown when the
                    shift is in the eligible state (mirrors the backend guard
                    in undoShiftActuals). Pushed to the right of the status
                    row so it reads as a distinct, deliberate action. */}
                {isAdmin && canUndoActuals(selectedShift) && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<UndoIcon fontSize="small" />}
                    onClick={handleOpenUndoConfirm}
                    sx={{ ml: 'auto' }}
                  >
                    Undo Actuals
                  </Button>
                )}
              </Box>

              {/* Cancellation / Replacement summary — only shown when relevant */}
              {(selectedShift.agencyCancelled || wasReplaced(selectedShift)) && (
                <Card variant="outlined" sx={{ mb: 3, borderColor: selectedShift.agencyCancelled ? red[300] : orange[300] }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SwapHorizIcon color={selectedShift.agencyCancelled ? 'error' : 'warning'} />
                      {selectedShift.agencyCancelled ? 'Cancellation Details' : 'Replacement Details'}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <List dense>
                      {selectedShift.agencyCancelled && (
                        <ListItem>
                          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                          <ListItemText
                            primary="Cancelled By"
                            secondary={selectedShift.cancelledByType || 'Unknown'}
                          />
                        </ListItem>
                      )}
                      {selectedShift.cancelledByType === 'Beaumont' && (
                        <ListItem>
                          <ListItemIcon><AccessTimeIcon fontSize="small" /></ListItemIcon>
                          <ListItemText
                            primary="12h Advance Notice Given"
                            secondary={selectedShift.advanceNoticeGiven === true ? 'Yes' : selectedShift.advanceNoticeGiven === false ? 'No' : 'Not recorded'}
                          />
                        </ListItem>
                      )}
                      {wasReplaced(selectedShift) && (
                        <ListItem>
                          <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
                          <ListItemText
                            primary="Replaced By"
                            secondary={supersededByLabel(selectedShift.supersededByShiftId) || 'Unknown'}
                          />
                        </ListItem>
                      )}
                      <ListItem>
                        <ListItemIcon><MonetizationOnIcon fontSize="small" /></ListItemIcon>
                        <ListItemText
                          primary="Charge Applicable"
                          secondary={selectedShift.chargeApplies ? 'Yes — agency was not given 12h notice' : 'No'}
                          secondaryTypographyProps={{
                            color: selectedShift.chargeApplies ? 'error.main' : 'text.secondary',
                            fontWeight: selectedShift.chargeApplies ? 'bold' : 'normal',
                          }}
                        />
                      </ListItem>
                    </List>
                    {wasReplaced(selectedShift) && supersededById(selectedShift.supersededByShiftId) && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/${isAdmin ? 'admin' : 'manager'}/shiftsWard/${supersededById(selectedShift.supersededByShiftId)}/edit`)}
                      >
                        Open Replacement Shift
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              <Grid container spacing={3}>
                {/* Actual Times */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon color="primary" />
                        Actual Times
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <EventIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Started"
                            secondary={formatDate(selectedShift.actualStart)}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <EventIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Finished"
                            secondary={formatDate(selectedShift.actualFinish)}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <AccessTimeIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Duration"
                            secondary={calculateDuration(selectedShift.actualStart, selectedShift.actualFinish)}
                          />
                        </ListItem>

                        <ListItem>
                          <ListItemIcon>
                            <MonetizationOnIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Billable Hours"
                            secondary={billableDisplayFor(selectedShift)}
                            secondaryTypographyProps={
                              beaumontShortNoticeCharge(selectedShift)
                                ? { color: 'error.main', fontWeight: 'bold' }
                                : isCancelledOrNotWorked(selectedShift)
                                  ? { color: 'text.disabled' }
                                  : undefined
                            }
                          />
                        </ListItem>

                        <ListItem>
                          <ListItemIcon>
                            <CheckCircleIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Outcome"
                            secondary={outcomeLabel(selectedShift)}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <PersonIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Approved By"
                            secondary={
                              selectedShift.stage2WardApprovalManager?.name
                                ? `${selectedShift.stage2WardApprovalManager.name}${selectedShift.stage2WardApprovalManager.email ? ` (${selectedShift.stage2WardApprovalManager.email})` : ''}`
                                : '⚠️ Not recorded'
                            }
                            secondaryTypographyProps={{
                              color: selectedShift.stage2WardApprovalManager?.name ? 'text.secondary' : 'warning.main',
                              fontWeight: selectedShift.stage2WardApprovalManager?.name ? 'normal' : 'bold',
                            }}
                          />
                        </ListItem>


                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Staff Info */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="primary" />
                        Staff Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <Avatar sx={{ width: 24, height: 24, bgcolor: green[500] }}>
                              <PersonIcon sx={{ fontSize: 16 }} />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary="Staff Member"
                            secondary={selectedShift.actualPersonWorked || 'N/A'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <WorkIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Category"
                            secondary={selectedShift.categoryOfStaff || 'N/A'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <WorkIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Staff / Agency"
                            secondary={
                              (selectedShift.actualFilledByAgency || selectedShift.filledByAgency)
                                ? `Agency — ${agencyNameOf(selectedShift) || 'Unknown agency'}`
                                : 'Staff'
                            }
                          />
                        </ListItem>
                        {(selectedShift.actualFilledByAgency || selectedShift.filledByAgency) && (
                          <ListItem>
                            <ListItemIcon>
                              <WorkIcon fontSize="small" sx={{ color: selectedShift.poNumber ? '#9c27b0' : '#ff9800' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary="PO Number"
                              secondary={selectedShift.poNumber || '⚠️ Not recorded'}
                              secondaryTypographyProps={{
                                fontWeight: selectedShift.poNumber ? 'bold' : 'normal',
                                color: selectedShift.poNumber ? 'text.primary' : 'warning.main',
                              }}
                            />
                          </ListItem>
                        )}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Location Info */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalHospitalIcon color="primary" />
                        Location Details
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <LocationOnIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Ward/Department"
                            secondary={selectedShift.wardDepartmentArea || 'N/A'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <WorkIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Directorate"
                            secondary={wardToDirectorateMap[selectedShift.wardDepartmentArea] || 'N/A'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <EventIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Reason for Request"
                            secondary={selectedShift.reasonForRequest || 'N/A'}
                          />
                        </ListItem>

                                                    {['1-1 special', 'Cohorts'].some(
                              r => selectedShift.reasonForRequest?.toLowerCase() === r.toLowerCase()
                            ) && (
                              <ListItem>
                                <ListItemIcon>
                                  <WorkIcon fontSize="small" sx={{ color: selectedShift.specialsReason ? '#9c27b0' : '#ff9800' }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Clinical Reason"
                                  secondary={
                                    selectedShift.specialsReason
                                      ? selectedShift.specialsReason
                                      : '⚠️ Not recorded'
                                  }
                                  secondaryTypographyProps={{
                                    color: selectedShift.specialsReason ? 'text.secondary' : 'warning.main',
                                    fontWeight: selectedShift.specialsReason ? 'normal' : 'bold',
                                  }}
                                />
                              </ListItem>
                            )}

                      </List>
                    </CardContent>
                  </Card>



                </Grid>

                {/* Scheduled Times */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon color="action" />
                        Scheduled Times
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <EventIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Scheduled Start"
                            secondary={formatDate(selectedShift.startDateTime)}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <EventIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Scheduled End"
                            secondary={formatDate(selectedShift.endDateTime)}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <AccessTimeIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Scheduled Duration"
                            secondary={calculateDuration(selectedShift.startDateTime, selectedShift.endDateTime)}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Additional Info */}
                {(selectedShift.specialRequirements || selectedShift.comments) && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Additional Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {selectedShift.specialRequirements && (
                          <Typography variant="body2" gutterBottom>
                            <strong>Special Requirements:</strong> {selectedShift.specialRequirements}
                          </Typography>
                        )}
                        {selectedShift.comments && (
                          <Typography variant="body2">
                            <strong>Comments:</strong> {selectedShift.comments}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Undo Actuals — Are You Sure confirmation */}
      <Dialog
        open={openUndoConfirm}
        onClose={handleCloseUndoConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Undo Actuals — Are You Sure?
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            This will undo the recorded actuals and approval for this shift and step it back to{' '}
            <strong>Open</strong>, ready to be re-approved. The actual start/finish time, staff
            member worked, approving manager, and any cancellation/charge details will all be
            cleared. This does not change who the shift is assigned to. Use this only to correct a
            mistake — for example, a shift that was approved as worked when it was not actually
            worked, or one that was marked Not-Worked in error. Cancelled or replaced shifts are
            not affected by this action.
          </DialogContentText>
          {selectedShift && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#fafafa' }}>
              <Typography variant="body2">
                <strong>Ward/Dept:</strong> {selectedShift.wardDepartmentArea || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Staff Member:</strong> {selectedShift.actualPersonWorked || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Actual:</strong> {formatDate(selectedShift.actualStart)} – {formatDate(selectedShift.actualFinish)}
              </Typography>
            </Paper>
          )}
          <textarea
            placeholder="Optional: add a reason for undoing this shift's actuals (recorded in the audit log)"
            value={undoReason}
            onChange={(e) => setUndoReason(e.target.value)}
            rows={3}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUndoConfirm} disabled={isUndoing}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUndoActuals}
            color="warning"
            variant="contained"
            disabled={isUndoing}
            startIcon={isUndoing ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}
          >
            {isUndoing ? 'Undoing…' : 'Yes, Undo Actuals'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShiftCompletedListScreen;